import { useAuth } from "@/hooks/use-auth-simple";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, ArrowLeft } from "lucide-react";
import { Link, Redirect } from "wouter";

type SafeUser = {
  id: string;
  username: string;
  isAdmin?: boolean;
  is_admin?: boolean;
  department?: string;
};

// Sample guidelines - will be replaced with database later
const sampleGuidelines = [
  {
    id: 'tls-flex-dscr-non-qm',
    title: 'TLS FLEX DSCR NON-QM GUIDELINES',
    description: 'Comprehensive guidelines for Flex DSCR Non-QM loan programs',
    lastUpdated: '8/15/2025'
  },
  {
    id: 'conventional-loans',
    title: 'Conventional Loans Guidelines',
    description: 'Standard conventional loan program guidelines',
    lastUpdated: '7/1/2025'
  },
  {
    id: 'fha-loans',
    title: 'FHA Loans Guidelines',
    description: 'Federal Housing Administration loan program guidelines',
    lastUpdated: '6/15/2025'
  },
  {
    id: 'va-loans',
    title: 'VA Loans Guidelines',
    description: 'Veterans Affairs loan program guidelines',
    lastUpdated: '5/20/2025'
  }
];

export default function GuidelinesListPage() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#1a1a1a]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-100"></div>
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/auth" />;
  }

  if (!(user?.isAdmin || (user as any)?.is_admin)) {
    return <Redirect to="/" />;
  }

  return (
    <div 
      className="min-h-screen bg-[#1a1a1a] text-gray-100" 
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)'
      }}
    >
      {/* Header */}
      <header 
        className="px-4 sm:px-6 h-14 border-b border-[#3a3a3a] bg-[#2a2a2a] sticky top-0 z-50"
        style={{ 
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          height: 'calc(56px + env(safe-area-inset-top, 0px))',
          paddingLeft: 'calc(16px + env(safe-area-inset-left, 0px))',
          paddingRight: 'calc(16px + env(safe-area-inset-right, 0px))'
        }}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center h-full">
          <div className="flex items-center gap-4" style={{ textAlign: 'left' }}>
            <Link href="/project-development">
              <Button 
                variant="ghost" 
                size="sm" 
                className="transition-all duration-200 rounded-md px-3 text-gray-300 hover:text-gray-100 hover:bg-[#3a3a3a] border border-[#3a3a3a] touch-manipulation"
                style={{ minHeight: '44px', fontSize: '16px', textAlign: 'center' }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Back to Ideas</span>
                <span className="sm:hidden">Back</span>
              </Button>
            </Link>
            <div className="border-l border-[#3a3a3a] pl-4 h-10 flex items-center">
              <div className="flex items-center gap-2 text-lg font-semibold text-gray-100" style={{ textAlign: 'left' }}>
                <FileText className="h-5 w-5 text-primary" />
                <span style={{ textAlign: 'left' }}>Guidelines Management</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" style={{ textAlign: 'left' }}>
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-100 mb-2" style={{ textAlign: 'left' }}>
            Guidelines Library
          </h1>
          <p className="text-gray-400" style={{ textAlign: 'left', lineHeight: '1.6' }}>
            Select a guideline document to view and edit its sections. Department heads can update content directly.
          </p>
        </div>

        {/* Guidelines Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sampleGuidelines.map((guideline) => (
            <Link key={guideline.id} href={`/guidelines/${guideline.id}`}>
              <Card 
                className="rounded-lg border border-[#3a3a3a] bg-[#2a2a2a] shadow-lg hover:shadow-xl transition-all duration-200 hover:border-[#4a4a4a] cursor-pointer group h-full"
                style={{ textAlign: 'left' }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="p-2 rounded-lg bg-[#3a3a3a] text-primary group-hover:bg-[#4a4a4a] transition-colors">
                      <FileText className="h-6 w-6" />
                    </div>
                  </div>
                  <CardTitle className="text-gray-100 text-lg font-semibold mb-1" style={{ textAlign: 'left' }}>
                    {guideline.title}
                  </CardTitle>
                  <CardDescription className="text-gray-400 text-xs" style={{ textAlign: 'left' }}>
                    Last updated: {guideline.lastUpdated}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300 text-sm leading-relaxed" style={{ textAlign: 'left', lineHeight: '1.6' }}>
                    {guideline.description}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Empty State Message (if no guidelines) */}
        {sampleGuidelines.length === 0 && (
          <Card className="rounded-lg border border-[#3a3a3a] bg-[#2a2a2a] p-12 text-center">
            <FileText className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-300 mb-2">No Guidelines Yet</h3>
            <p className="text-gray-500">Check back soon for guideline documents.</p>
          </Card>
        )}
      </main>
    </div>
  );
}

